import {
  makeObservable,
  observable,
  ObservableMap,
  reaction,
  runInAction,
  toJS,
  action,
  computed,
} from "mobx";
import { api } from "@/modules/api";
import { AccountObservable } from "@/store/account/observables/AccountObservable";
import { CacheKey } from "@/modules/cache/constants";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { AsyncResult } from "@/modules/async-result/types";
import { Uuid } from "@/domains/global/identifiers";
import { asyncResultModule } from "@/modules/async-result";
import { BaseError, RuntimeAssertionError } from "@/domains/errors";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { IntervalObservable } from "@/store/common/IntervalObservable";
import { DateTime, Duration } from "luxon";

export class AppStoreAccountStore extends AppSubStore {
  private static readonly REFRESH_INTERVAL = Duration.fromObject({ hours: 4 });

  accountMap: ObservableMap<Uuid, AccountObservable>;
  fetchMyAccountState: AsyncResult<Uuid>;

  private initialized = false;
  private inflightFetch: Promise<void> | null = null;
  private fetchGeneration = 0;
  private lastSuccessfulFetchAt: DateTime | null = null;
  private refreshTimer: IntervalObservable | null = null;
  private refreshDisposer: (() => void) | null = null;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    this.accountMap = new ObservableMap();
    this.fetchMyAccountState = asyncResultModule.setInitial();

    makeObservable<
      AppStoreAccountStore,
      | "getAccount"
      | "fetchMyAccount"
      | "persistMyAccountToCache"
      | "initialized"
      | "fakeAccount"
      | "inflightFetch"
      | "fetchGeneration"
      | "lastSuccessfulFetchAt"
      | "refreshTimer"
      | "refreshDisposer"
      | "initializeRefreshTimer"
      | "disposeRefreshTimer"
      | "refreshMyAccountIfStale"
    >(this, {
      fakeAccount: false,
      getAccount: false,
      fetchMyAccount: action,
      persistMyAccountToCache: action,
      inflightFetch: false,
      fetchGeneration: false,
      lastSuccessfulFetchAt: false,
      refreshTimer: false,
      refreshDisposer: false,
      initializeRefreshTimer: false,
      disposeRefreshTimer: false,
      refreshMyAccountIfStale: false,
      hydrateMyAccountFromCache: action,
      initialize: action,
      myAccountId: computed,
      myAccount: computed,
      resetState: action,
      accountMap: observable,
      fetchMyAccountState: observable,
      initialized: observable,
    });
  }

  private getAccount(accountId: Uuid): AccountObservable | undefined {
    return this.accountMap.get(accountId);
  }

  private async fetchMyAccount() {
    if (this.inflightFetch) {
      return this.inflightFetch;
    }

    const generation = ++this.fetchGeneration;

    const request = (async () => {
      runInAction(() => {
        this.fetchMyAccountState.called = true;
        this.fetchMyAccountState.error = undefined;
        this.fetchMyAccountState.loading = true;
      });

      try {
        const result = await api.get("/v1/me/account", {});

        if (!result.data) {
          throw new BaseError({
            message: "[AccountStore] Failed to fetch current account.",
            info: {
              result: objectModule.safeAsJson(result),
            },
          });
        }

        const accountData = result.data;

        const {
          id,
          profile_email_address,
          profile_display_name,
          profile_photo_url,
          created_at,
          ...settings
        } = accountData;

        if (!id) {
          throw new RuntimeAssertionError({
            message: "[AccountStore.fetchMyAccount] Account data did not contain an id.",
            info: { accountData },
          });
        }

        // If resetState() was called while this fetch was in-flight, skip writing stale data.
        if (this.fetchGeneration !== generation) {
          return;
        }

        runInAction(() => {
          const account = new AccountObservable({
            id,
            profileEmailAddress: profile_email_address,
            profileDisplayName: profile_display_name,
            profilePhotoUrl: profile_photo_url ?? null,
            createdAt: created_at,
            settings,
          });

          this.accountMap.set(account.id, account);
          this.fetchMyAccountState.data = account.id;
          this.lastSuccessfulFetchAt = DateTime.now();
        });

        await this.persistMyAccountToCache();
      } catch (unknownErr) {
        const err =
          unknownErr instanceof BaseError
            ? unknownErr
            : new BaseError({
                message: "[AccountStore] Unexpected error while fetching account.",
                originalError: unknownErr as Error,
              });

        runInAction(() => {
          this.fetchMyAccountState.error = err;
        });

        logger.error({
          message: "[AccountStore] fetchMyAccount failed",
          info: {
            err: objectModule.safeErrorAsJson(err),
          },
        });
      } finally {
        runInAction(() => {
          this.fetchMyAccountState.loading = false;
          this.inflightFetch = null;
        });
      }
    })();

    this.inflightFetch = request;

    return request;
  }

  private async persistMyAccountToCache() {
    await this.store.accountCache.set(CacheKey.MyAccount, toJS(this.myAccount));
  }

  async hydrateMyAccountFromCache(): Promise<boolean> {
    const myAccount = await this.store.accountCache.get(CacheKey.MyAccount);

    if (!myAccount) {
      return false;
    }

    const account = new AccountObservable({
      id: myAccount.id,
      profileEmailAddress: myAccount.profileEmailAddress,
      profileDisplayName: myAccount.profileDisplayName,
      profilePhotoUrl: myAccount.profilePhotoUrl ?? null,
      createdAt: myAccount.createdAt,
      settings: myAccount.settings,
    });

    runInAction(() => {
      this.accountMap.set(account.id, account);
      this.fetchMyAccountState.data = account.id;
    });

    return true;
  }

  async initialize() {
    this.initialized = true;

    const fetchMyAccountPromise = this.fetchMyAccount();
    const hydrationSuccess = await this.hydrateMyAccountFromCache();
    this.initializeRefreshTimer();

    /**
     * If we were able to hydrate from cache, we don't need to wait for the API call.
     */
    if (hydrationSuccess) {
      return;
    }

    await fetchMyAccountPromise;

    if (this.fetchMyAccountState.error) {
      throw new RuntimeAssertionError({
        message: "[AccountStore.initialize] Failed.",
        info: { originalError: objectModule.safeErrorAsJson(this.fetchMyAccountState.error) },
      });
    }
  }

  get myAccountId(): Uuid {
    // In case we are trying to access after the store has been unmounted
    if (!this.initialized) {
      return this.fakeAccount.id;
    }

    const accountId = this.fetchMyAccountState.data;

    if (!accountId) {
      throw new RuntimeAssertionError({
        message:
          "[AccountStore.myAccountId] accountId was not set - make sure that `initialize` was called.",
      });
    }

    return accountId;
  }

  get myAccount(): AccountObservable {
    // In case we are trying to access after the store has been unmounted
    if (!this.initialized) {
      return this.fakeAccount;
    }

    const account = this.getAccount(this.myAccountId);

    if (!account) {
      throw new RuntimeAssertionError({
        message:
          "[AccountStore.myAccount] account was not set - make sure that `initialize` was called.",
      });
    }

    return account;
  }

  resetState() {
    this.disposeRefreshTimer();

    // Cancel any inflight fetch before resetting state so completion handlers bail out.
    this.inflightFetch = null;
    this.lastSuccessfulFetchAt = null;

    runInAction(() => {
      this.initialized = false;
      this.accountMap = new ObservableMap();
      this.fetchMyAccountState = asyncResultModule.setInitial();
    });
  }

  private get fakeAccount(): AccountObservable {
    return new AccountObservable({
      id: "__FAKE_ACCOUNT_ID__",
      profileEmailAddress: "__FAKE_ACCOUNT_EMAIL_ADDRESS__",
      profileDisplayName: "__FAKE_ACCOUNT_DISPLAY_NAME__",
      profilePhotoUrl: null,
      createdAt: null,
      settings: {},
    });
  }

  private initializeRefreshTimer() {
    if (this.refreshTimer) {
      return;
    }

    this.refreshTimer = new IntervalObservable({
      duration: AppStoreAccountStore.REFRESH_INTERVAL,
      jitter: Duration.fromObject({ minutes: 5 }),
    });

    this.refreshDisposer = reaction(
      () => this.refreshTimer?.value,
      () => {
        void this.refreshMyAccountIfStale();
      },
      { fireImmediately: true }
    );
  }

  private disposeRefreshTimer() {
    this.refreshDisposer?.();
    this.refreshDisposer = null;
    this.refreshTimer?.dispose();
    this.refreshTimer = null;
  }

  private async refreshMyAccountIfStale(): Promise<void> {
    if (!this.initialized || this.inflightFetch) {
      return;
    }

    const refreshThreshold = DateTime.now().minus(AppStoreAccountStore.REFRESH_INTERVAL);
    if (this.lastSuccessfulFetchAt && this.lastSuccessfulFetchAt > refreshThreshold) {
      return;
    }

    try {
      await this.fetchMyAccount();
    } catch (unknownErr) {
      logger.warn({
        message: "[AccountStore] refreshMyAccountIfStale failed",
        info: {
          err: objectModule.safeErrorAsJson(unknownErr as BaseError),
        },
      });
    }
  }
}
